// src/services/pushService.js
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, app } from "../firebase";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const SERVICE_WORKER_READY_TIMEOUT_MS = 10000;

let swRegistrationPromise = null;
let messagingPromise = null;
let ensureTokenState = { promise: null, userId: null };

function waitForServiceWorkerActivation(registration, timeoutMs) {
  if (registration.active?.state === "activated") {
    return Promise.resolve(registration);
  }

  const worker = registration.installing || registration.waiting || registration.active;
  if (!worker) return Promise.resolve(null);

  return new Promise((resolve) => {
    const handleStateChange = () => {
      if (worker.state === "activated") {
        clearTimeout(timeoutId);
        worker.removeEventListener("statechange", handleStateChange);
        resolve(registration);
      }
    };

    const timeoutId = setTimeout(() => {
      worker.removeEventListener("statechange", handleStateChange);
      resolve(null);
    }, timeoutMs);

    worker.addEventListener("statechange", handleStateChange);

    if (worker.state === "activated") {
      clearTimeout(timeoutId);
      worker.removeEventListener("statechange", handleStateChange);
      resolve(registration);
    }
  });
}

async function ensureMessagingServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;

  if (!swRegistrationPromise) {
    swRegistrationPromise = (async () => {
      const baseUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
      const swUrl = `${baseUrl}/firebase-messaging-sw.js`;
      const registration = await navigator.serviceWorker.register(swUrl);
      const activeRegistration = await waitForServiceWorkerActivation(
        registration,
        SERVICE_WORKER_READY_TIMEOUT_MS,
      );
      if (!activeRegistration) {
        throw new Error("Service worker activation timed out");
      }
      return activeRegistration;
    })().catch((error) => {
      swRegistrationPromise = null;
      console.error("FCM service worker registration failed:", error);
      return null;
    });
  }

  return swRegistrationPromise;
}

async function getMessagingInstance() {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const supported = await isSupported();
      if (!supported) return null;
      return getMessaging(app);
    })().catch((error) => {
      messagingPromise = null;
      console.error("Firebase Messaging init failed:", error);
      return null;
    });
  }

  return messagingPromise;
}

export async function ensureFcmToken() {
  const user = auth.currentUser;
  if (!user) {
    ensureTokenState = { promise: null, userId: null };
    return null;
  }
  if (typeof Notification === "undefined") return null;
  if (!VAPID_KEY) {
    console.error("FCM VAPID key is not configured.");
    return null;
  }

  if (ensureTokenState.userId !== user.uid) {
    ensureTokenState = { promise: null, userId: user.uid };
  }

  if (Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;
  }
  if (Notification.permission !== "granted") return null;

  if (!ensureTokenState.promise) {
    ensureTokenState.promise = (async () => {
      const userId = user.uid;
      const [messaging, swReg] = await Promise.all([
        getMessagingInstance(),
        ensureMessagingServiceWorker(),
      ]);

      if (!messaging || !swReg) return null;

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });

      if (!token) return null;
      if (auth.currentUser?.uid !== userId) return null;

      const userRef = doc(db, "users", userId);
      const existing = await getDoc(userRef);
      const currentToken = existing.exists() ? existing.data().fcmToken : null;
      if (currentToken !== token) {
        await setDoc(
          userRef,
          { fcmToken: token, fcmTokenUpdatedAt: serverTimestamp() },
          { merge: true },
        );
      }

      return token;
    })().finally(() => {
      ensureTokenState.promise = null;
    });
  }

  return ensureTokenState.promise;
}

export async function onForegroundMessage(handler) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
